/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import compact from "lodash/compact";
import filter from "lodash/filter";
import map from "lodash/map";
import some from "lodash/some";

import { FilterValueFieldNames } from "Constants/FilterFields";
import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IMVPUserProfile } from "Interfaces/MVP/IMVPUserProfile";
import store from "Store";

/**
 * Returns loading status of user profile.
 * @param appState
 * @returns {boolean}
 */
export const getIsLoadingStatus = (state: AppState): boolean =>
  state.mvpState.userProfilesSearchState.searchOperationStatus.read === OperationStatus.Pending;

/**
 * Returns the filtered user profiles.
 * @param appState
 * @returns {boolean}
 */
export const getFilteredUserProfiles = (state: AppState): IMVPUserProfile[] =>
  state.mvpState.userProfilesSearchState.filteredUserProfiles;

/**
 * Returns the filtered count of user profiles.
 * @param appState
 * @returns {boolean}
 */
export const getFilteredCount = (state: AppState): number =>
  state.mvpState.userProfilesSearchState.filteredCount;

/**
 * Returns the selected filters.
 * @param appState
 * @returns {IFilterValue[]}
 */
export const getSelectedFilterValues = (state: AppState): IFilterValue[] =>
  state.mvpState.userProfilesSearchState.searchFilterValues;

/**
 * Returns the search text.
 * @param appState
 * @returns {string}
 */
export const getSearchTextValue = (state: AppState): string =>
  state.mvpState.userProfilesSearchState.searchText;

/**
 * Returns the pagination page size.
 * @param appState
 * @returns {number}
 */
export const getPaginationPageSize = (state: AppState): number =>
  state.mvpState.userProfilesSearchState.pageSize;

/**
 * Returns the pagination current page.
 * @param appState
 * @returns {number}
 */
export const getPaginationCurrentPage = (state: AppState): number =>
  state.mvpState.userProfilesSearchState.currentPage;

/**
 * Returns the sort column.
 * @param appState
 * @returns {string}
 */
export const getSortColumn = (state: AppState): string | undefined =>
  state.mvpState.userProfilesSearchState.sortColumn;

/**
 * Returns the sort descending.
 * @param appState
 * @returns {boolean}
 */
export const getSortDescending = (state: AppState): boolean | undefined =>
  state.mvpState.userProfilesSearchState.sortDescending;

/**
 * Selects the technology area filter values by area groups.
 * @param {string[]} areaGroups
 * @returns {IFilterValue[]}
 */
export const selectTechnologyAreaFilterValuesByAreaGroups = (
  areaGroups: string[]
): IFilterValue[] => {
  const rootState = store.getState();
  const techAreas = filter(rootState.technologyAreaDataState.technologyAreaData, (y) =>
    some(areaGroups, (x) => x === y.technologyFocusAreaGroupName)
  );
  return compact(
    map(techAreas, (item) => {
      return { fieldName: FilterValueFieldNames.TECHNOLOGY_AREA, value: item.technologyName };
    })
  );
};
